import { isNil } from 'lodash';
import { memo } from 'react';
import { Field } from '@shared/data/entity/fields/Field';
import { isInputButtonComponent, isTimeTrackerComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { CollectionViewAnalytics } from '../../CollectionViewAnalytics';
import { InputButton } from '../staticComponents/InputButton';
import { AddressCollectionViewField } from './AddressCollectionViewField';
import { CombinedTagsCollectionViewField } from './CombinedTagsCollectionViewField';
import { DateCollectionViewField } from './DateCollectionViewField';
import { DateRangeCollectionViewField } from './DateRangeCollectionViewField';
import { EmailCollectionViewField } from './EmailCollectionViewField';
import { EntityBatchCollectionViewField } from './EntityBatchCollectionViewField';
import { FileCollectionViewField } from './FileCollectionViewField';
import { GeolocationCollectionViewField } from './GeolocationCollectionViewField';
import { NumberCollectionViewField } from './NumberCollectionViewField';
import { PersonCollectionViewField } from './PersonCollectionViewField';
import { RelatedCardCollectionViewField } from './RelatedCardCollectionViewField';
import { RelatedLookupCollectionViewField } from './RelatedLookupCollectionViewField';
import { ScheduledAutomationCollectionViewField } from './ScheduledAutomationCollectionViewField';
import { SignatureCollectionViewField } from './SignatureCollectionViewField';
import { SubformCollectionViewField } from './SubformCollectionViewField';
import { TagCollectionViewField } from './TagCollectionViewField';
import { TextCollectionViewField } from './TextCollectionViewField';
import { TimeTrackerCollectionViewField } from './TimeTrackerCollectionViewField';
import { TodoCollectionViewField } from './TodoCollectionViewField';
import { UnknownCollectionViewField } from './UnknownCollectionViewField';
import { UrlCollectionViewField } from './UrlCollectionViewField';

interface Props {
  field?: Field | null;
  component: API.AnyComponent;
  workflowTemplateId: string;
  entityId: string;
  showLabel: boolean;
  canUpdateCard: boolean;
  analytics?: CollectionViewAnalytics;
}

export const CardCollectionViewField = memo(function CardCollectionViewFieldComponent({
  field,
  component,
  workflowTemplateId,
  entityId,
  showLabel,
  canUpdateCard,
  analytics,
}: Readonly<Props>) {
  if (isInputButtonComponent(component)) {
    return (
      <InputButton
        component={component}
        workflowTemplateId={workflowTemplateId}
        entityId={entityId}
        withLabel={showLabel}
        canUpdateCard={canUpdateCard}
        analytics={analytics}
      />
    );
  }

  if (isNil(field)) {
    return null;
  }

  switch (field.fieldType) {
    case 'AddressField':
      return <AddressCollectionViewField field={field} />;
    case 'CombinedTagsField':
      return <CombinedTagsCollectionViewField field={field} />;
    case 'DateField':
      return <DateCollectionViewField field={field} />;
    case 'DateRangeField':
      return <DateRangeCollectionViewField field={field} />;
    case 'EmailField':
      return <EmailCollectionViewField field={field} />;
    case 'EntityBatch':
      return <EntityBatchCollectionViewField field={field} />;
    case 'FileField':
      return <FileCollectionViewField field={field} />;
    case 'GeolocationField':
      return <GeolocationCollectionViewField field={field} />;
    case 'NumberField':
      return <NumberCollectionViewField field={field} />;
    case 'PersonField':
      return <PersonCollectionViewField field={field} />;
    case 'RelatedEntityField':
      return <RelatedCardCollectionViewField field={field} component={component} />;
    case 'RelatedLookupField':
      return (
        <RelatedLookupCollectionViewField
          field={field}
          component={component}
          workflowTemplateId={workflowTemplateId}
        />
      );
    case 'ScheduledAutomationField':
      return <ScheduledAutomationCollectionViewField field={field} />;
    case 'SignatureField':
      return <SignatureCollectionViewField field={field} />;
    case 'SubformField':
      return <SubformCollectionViewField field={field} />;
    case 'TagField':
      return <TagCollectionViewField field={field} />;
    case 'TextField':
      return <TextCollectionViewField field={field} />;
    case 'TimeTrackerField':
      return (
        <>
          {isTimeTrackerComponent(component) && (
            <TimeTrackerCollectionViewField
              component={component}
              entityId={entityId}
              field={field}
              canUpdateCard={canUpdateCard}
            />
          )}
        </>
      );
    case 'TodoField':
      return <TodoCollectionViewField field={field} />;
    case 'UrlField':
      return <UrlCollectionViewField field={field} />;
    default:
      return <UnknownCollectionViewField field={field} />;
  }
});
